import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import moment from 'moment'
import DetailsBox from '../../../components/Cards/DetailsBox'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import API from '../../../services/API'
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Chip,
  IconButton,
  LinearProgress,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CloseIcon from '@mui/icons-material/Close'
import { GlobalContext } from '../../../App'
import useIsMobile from '../../../helpers/useIsMobile'

const SelectMatchingApplications = ({ shiftID, onClose, loadApplications }) => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [selectionModel, setSelectionModel] = useState([])
  const [confirmAccept, setConfirmAccept] = useState(false)
  const [confirmReject, setConfirmReject] = useState(false)
  const [rejectedReason, setRejectedReason] = useState('')
  const [reviewError, setReviewError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const { setToast } = useContext(GlobalContext)
  const isMobile = useIsMobile('sm')

  const load = () => {
    setLoading(true)
    API.get(`/matching-shift-applications?shiftID=${shiftID}`)
      .then((res) => {
        if (res.status === 200) {
          setData(res.data)
          setSelectionModel([])
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftID])

  const rows = data?.applications ?? []
  const remainingSlots = data?.openCount ?? 0
  const selectOldestCount = Math.min(remainingSlots, rows.length)

  const selectOldest = () => {
    const oldestFirst = [...rows].sort(
      (a, b) => moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf(),
    )
    setSelectionModel(
      oldestFirst.slice(0, remainingSlots).map((row) => row._id),
    )
  }

  const columns = useMemo(
    () => [
      {
        field: 'employee',
        headerName: 'Applicant',
        flex: 1,
        minWidth: isMobile ? 140 : undefined,
        renderCell: (params) => {
          const userAccount = params.row.employee?.userAccount ?? {}
          return (
            <Stack direction="row" gap={1} alignItems="center" minWidth={0}>
              <Avatar
                src={userAccount.photograph}
                sx={{ width: 28, height: 28, flexShrink: 0 }}
              />
              <Stack minWidth={0}>
                <Typography variant="body2" noWrap>
                  {userAccount.firstName} {userAccount.lastName}
                </Typography>
                {/* Phones hide the Applied column - show it under the name */}
                {isMobile && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    Applied {moment(params.row.createdAt).format('D MMM HH:mm')}
                  </Typography>
                )}
              </Stack>
            </Stack>
          )
        },
      },
      {
        field: 'createdAt',
        headerName: 'Applied',
        width: 150,
        valueFormatter: (value) => moment(value).format('Do MMM HH:mm'),
      },
      {
        field: 'employeeConflicts',
        headerName: 'Conflicts',
        flex: isMobile ? undefined : 1,
        width: isMobile ? 110 : undefined,
        sortable: false,
        renderCell: (params) =>
          params.value?.length > 0 ? (
            <Tooltip
              title={params.value
                .map(
                  (c) =>
                    `${c?.event?.title ?? c?.shiftType ?? 'Shift'}: ${moment(
                      c.start,
                    ).format('DD/MM HH:mm')} - ${moment(c.end).format(
                      'DD/MM HH:mm',
                    )}`,
                )
                .join(', ')}
            >
              <Chip
                size="small"
                color="error"
                icon={<WarningAmberIcon />}
                label={`${params.value.length} conflict${
                  params.value.length === 1 ? '' : 's'
                }`}
              />
            </Tooltip>
          ) : (
            <Chip size="small" color="success" label="None" />
          ),
      },
    ],
    [isMobile],
  )

  const submitReview = (accepted) => {
    setSubmitting(true)
    setReviewError(null)

    API.post(
      '/shift-set-review',
      {
        shiftID,
        acceptedApplicationIDs: accepted ? selectionModel : [],
        rejectRemaining: !accepted,
        rejectedReason: !accepted ? rejectedReason : undefined,
      },
      { skipGlobalErrorHandler: true },
    )
      .then((res) => {
        if (res.status === 200) {
          setConfirmAccept(false)
          setConfirmReject(false)
          setToast({
            message: accepted
              ? 'Applicants Allocated'
              : 'Remaining Applicants Rejected',
            severity: 'success',
          })
          loadApplications()
          if (accepted && remainingSlots - selectionModel.length <= 0) {
            onClose()
          } else {
            load()
          }
        } else if (
          res.status === 400 &&
          res.data?.errorCode === 'shiftAssignmentBlocked'
        ) {
          setReviewError({
            title: 'Some applicants cannot be allocated',
            message:
              'Resolve the following issues, or deselect these applicants, and try again.',
            applicationIssues: res.data?.applicationIssues ?? [],
          })
        } else {
          setReviewError({
            title: 'Unable to review applications',
            message:
              res.data?.error ||
              res.data?.message ||
              'Unable to perform action. Please try again later.',
          })
        }
      })
      .catch((error) => {
        setReviewError({
          title: 'Unable to review applications',
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            'Unable to perform action. Please try again later.',
        })
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <Stack
      sx={{ width: { xs: '100vw', sm: 620 }, maxWidth: '100vw', boxSizing: 'border-box', p: 1 }}
      gap={2}
    >
      {loading && <LinearProgress />}
      {!loading && data && (
        <DetailsBox
          title={`Applicants for ${data.shift?.clinicalRole ?? 'this vacancy'}`}
          buttonRight={
            isMobile && (
              <IconButton aria-label="Close" onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            )
          }
          rows={[
            {
              label: data.shift?.event ? 'Event' : 'Type',
              value: data.shift?.event ? (
                <Link to={`/events/${data.shift.event._id}`} target="_blank">
                  {data.shift.event.title}
                </Link>
              ) : (
                data.shift?.shiftType
              ),
              fullWidth: true,
            },
            {
              label: 'Start',
              value: moment(data.shift?.start).format('Do MMM - HH:mm'),
            },
            {
              label: 'End',
              value: moment(data.shift?.end).format('Do MMM - HH:mm'),
            },
            {
              label: 'Vacancies',
              value: `${data.filledCount} of ${data.quantity} filled`,
            },
          ]}
        >
          <Stack gap={1.5}>
            {rows.length === 0 ? (
              <Alert severity="success">
                No pending applications remain for this vacancy - they were
                likely just accepted or rejected elsewhere. You can close this
                panel.
              </Alert>
            ) : (
              <>
                <Alert severity={remainingSlots > 0 ? 'info' : 'warning'}>
                  <Stack gap={1}>
                    <>
                      {remainingSlots} open slot{remainingSlots === 1 ? '' : 's'}{' '}
                      remaining · {rows.length} pending applicant
                      {rows.length === 1 ? '' : 's'}. Select up to{' '}
                      {remainingSlots} to allocate.
                    </>
                    <Box justifyContent="center" width="100%" display="flex">
                      <Button
                        size="small"
                        onClick={selectOldest}
                        disabled={submitting}
                        variant="contained"
                      >
                        Select Oldest {selectOldestCount}
                      </Button>
                    </Box>
                  </Stack>
                </Alert>
                <Box sx={{ height: 340 }}>
                  <CustomDataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row._id}
                    checkboxSelection
                    hideToolbar
                    columnVisibilityModel={{ createdAt: !isMobile }}
                    disableRowSelectionOnClick
                    rowSelectionModel={selectionModel}
                    isRowSelectable={(params) =>
                      selectionModel.includes(params.id) ||
                      selectionModel.length < remainingSlots
                    }
                    onRowSelectionModelChange={(model) =>
                      setSelectionModel(model)
                    }
                  />
                </Box>
                {reviewError && (
                  <Alert severity="error">
                    <AlertTitle>{reviewError.title}</AlertTitle>
                    <Typography variant="body2">
                      {reviewError.message}
                    </Typography>
                    {reviewError.applicationIssues?.length > 0 && (
                      <ul>
                        {reviewError.applicationIssues.map((entry) => {
                          const applicant = rows.find(
                            (row) => row._id === entry.application,
                          )
                          const name = applicant
                            ? `${applicant.employee?.userAccount?.firstName ?? ''} ${
                                applicant.employee?.userAccount?.lastName ?? ''
                              }`.trim()
                            : 'Applicant'
                          return (
                            <li key={entry.application}>
                              <b>{name}: </b>
                              {entry.issues.map((issue) => issue.message).join(' ')}
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </Alert>
                )}
                <ButtonGroup
                  fullWidth
                  variant="contained"
                  orientation={isMobile ? 'vertical' : 'horizontal'}
                >
                  <Button
                    color="success"
                    disabled={submitting || selectionModel.length === 0}
                    onClick={() => {
                      setReviewError(null)
                      setConfirmAccept(true)
                    }}
                  >
                    Accept Selected ({selectionModel.length})
                  </Button>
                  <Button
                    color="error"
                    disabled={submitting || rows.length === 0}
                    onClick={() => {
                      setReviewError(null)
                      setConfirmReject(true)
                    }}
                  >
                    Reject Remaining Applicants
                  </Button>
                </ButtonGroup>
              </>
            )}
          </Stack>
        </DetailsBox>
      )}
      <Modal
        open={confirmAccept || confirmReject}
        onClose={() => {
          setConfirmAccept(false)
          setConfirmReject(false)
        }}
      >
        <Alert
          severity="info"
          icon={false}
          onClose={() => {
            setConfirmAccept(false)
            setConfirmReject(false)
          }}
        >
          <AlertTitle>
            {confirmAccept
              ? `Allocate ${selectionModel.length} selected applicant${
                  selectionModel.length === 1 ? '' : 's'
                }?`
              : 'Reject all remaining pending applicants?'}
          </AlertTitle>
          <Stack
            gap={1}
            component="form"
            onSubmit={(e) => {
              e.preventDefault()
              submitReview(confirmAccept)
            }}
          >
            {confirmReject && (
              <Autocomplete
                freeSolo
                options={[
                  'Shift already filled.',
                  'Staff no longer available',
                  'Event Cancelled',
                ]}
                value={rejectedReason}
                inputValue={rejectedReason}
                onChange={(e, value) => setRejectedReason(value || '')}
                onInputChange={(e, value) => setRejectedReason(value)}
                renderInput={(props) => (
                  <TextField required {...props} label="Reason" size="small" />
                )}
              />
            )}
            {confirmAccept &&
              'Selected applicants will be allocated to open slots and notified. This ignores non-blocking scheduling warnings.'}
            <ButtonGroup size="small" variant="outlined" fullWidth>
              <Button color="success" type="submit" disabled={submitting}>
                {submitting ? 'Working...' : 'Yes'}
              </Button>
              <Button
                color="error"
                disabled={submitting}
                onClick={() => {
                  setConfirmAccept(false)
                  setConfirmReject(false)
                }}
              >
                No
              </Button>
            </ButtonGroup>
          </Stack>
        </Alert>
      </Modal>
    </Stack>
  )
}

export default SelectMatchingApplications
